import { ReactNode, useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

const spin = keyframes`
  to { transform: rotate(360deg); }
`

// `animation: none` when idle — pausing would freeze the icon mid-rotation
// and flicker on the next click.
const Wrap = styled.span<{ $spinning: boolean }>`
  display: inline-flex;
  animation: ${({ $spinning }) =>
    $spinning
      ? css`
          ${spin} 0.8s linear infinite
        `
      : 'none'};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

type SpinProps = {
  spinning: boolean
  children: ReactNode
}

// Spins children while `spinning` is true, but only stops at full-rotation
// boundaries so the icon never snaps back mid-spin and a fast fetch still
// shows at least one complete rotation.
export function Spin({ spinning, children }: SpinProps) {
  const [animating, setAnimating] = useState(spinning)
  const spinningRef = useRef(spinning)

  useEffect(() => {
    spinningRef.current = spinning
    if (spinning) {
      setAnimating(true)
    }
  }, [spinning])

  return (
    <Wrap
      $spinning={animating}
      onAnimationIteration={() => {
        if (!spinningRef.current) {
          setAnimating(false)
        }
      }}
    >
      {children}
    </Wrap>
  )
}
